import { readCookie } from "modules/cookieManager";
import CompletionSuggestionType from "enums/completionSuggestionType";
import StringExtensions from "utils/stringExtensions";
import LocationType from "../../searchResultsFacets/enums/locationType";

/**
 * A utility class for search bar related helper functions.
 */
export default class SearchBarHelper {
  /**
   * Trims leading and trailing whitespace from the given text.
   * If the text is null or undefined, returns an empty string.
   * @param {string} text - The text to trim.
   * @returns {string} The trimmed text, or an empty string if the input is null or undefined.
   */
  static trimText(text) {
    return text?.trim() ?? '';
  }

  /**
   * Checks if the provided text is null, undefined, or consists only of whitespace characters.
   * @param {string} text - The text to check.
   * @returns {boolean} True if the text is null, undefined, or whitespace; otherwise, false.
   */
  static isNullOrWhiteSpace(text) {
    return (text?.trim().length ?? 0) === 0;
  }

  /**
   * Retrieves the last search text from the 'Personal' cookie.
   * @returns {string} The last search text, or an empty string if not found.
   */
  static getLastSearchText() {
    const personalCookie = readCookie('Personal', true);
    const parameters = new URLSearchParams(personalCookie);
    return parameters.get('searchtext') || '';
  }

  /**
   * Derives the formatted search text from a **Redux state** location object.
   *
   * Called once on SRP page load (searchBarInitializer) to populate the search
   * bar from the URL rather than a potentially stale cookie.
   * The formatting mirrors the server-side logic in UserNavigationAttribute.
   *
   * NOTE: This function operates on the Redux/server-side location shape, which
   * differs from the autocomplete suggestion shape used by getFormattedSearchText.
   * The two functions are NOT interchangeable:
   *   - locationType values use lowercase keys ('county', 'zip', …)
   *   - Name fields are per-type (location.county, location.zip, location.schoolDistrictName, …)
   *   - State comes from location.marketState for all location types except State,
   *     where it comes from location.stateName
   *
   * @param {Object} location - The location object from Redux state (state.searchParameters.location).
   * @returns {string} The formatted display text, or an empty string if unavailable.
   */
  static getSearchTextFromState(location) {
    if (!location || !location.locationType) {
      return '';
    }

    let formattedSearchText = '';

    switch (location.locationType) {
      case LocationType.Neighborhood.name:
        formattedSearchText = SearchBarHelper.trimText(
          `${location.neighborhood} Neighborhood, ${location.marketName}, ${location.marketState}`
        );
        break;
      case LocationType.County.name:
        formattedSearchText = SearchBarHelper.trimText(
          `${location.county} County, ${location.marketState}`
        );
        break;
      case LocationType.Zip.name:
        formattedSearchText = SearchBarHelper.trimText(location.zip);
        break;
      case LocationType.City.name:
        formattedSearchText = SearchBarHelper.trimText(
          `${location.city}, ${location.marketState}`
        );
        break;
      case LocationType.SchoolDistrict.name:
        formattedSearchText = SearchBarHelper.trimText(location.schoolDistrictName);
        break;
      case LocationType.State.name:
        formattedSearchText = SearchBarHelper.trimText(location.stateName);
        break;
      case LocationType.Market.name:
        formattedSearchText = SearchBarHelper.trimText(
          `${location.marketName}, ${location.marketState} Area`
        );
        break;
      default:
        formattedSearchText = '';
    }

    return formattedSearchText;
  }

  /**
   * Determines whether to hide the "Use my current location" option based on the search text and a force hide flag.
   * @param {string} searchText - The current search text input by the user.
   * @param {boolean} forceHide - Whether to forcefully hide the location option.
   * @returns {boolean} True if the location option should be hidden, false otherwise.
   */
  static shouldHideCurrentLocation(searchText, forceHide = false) {
    return forceHide || !SearchBarHelper.isNullOrWhiteSpace(searchText);
  }

  /**
   * Extracts the results array from the search results object.
   * @param {Object} searchResults - The search results object.
   * @returns {Array} The extracted results array, or an empty array if not present.
   */
  static extractResults(searchResults) {
    return searchResults?.Results ?? [];
  }

  /**
   * Formats the search text from an **autocomplete suggestion** object.
   *
   * Called in searchBar.js when the user picks a result from the typeahead
   * dropdown (selectedResultData). The input shape comes from the search
   * completion API and differs from the Redux state location shape:
   *   - completionSuggestionType values use full API names ('CountyEquivalent', 'PostalCode', …)
   *   - All name-like fields are unified under location.name
   *   - State comes from location.marketState
   *
   * Use getSearchTextFromState for the Redux/server-side location shape.
   *
   * @param {Object} location - A completion suggestion object (selectedResultData).
   * @returns {string} The formatted search text.
   */
  static getFormattedSearchText(location) {
    if (!location) {
      return '';
    }

    const locationType = location.completionSuggestionType;
    let formattedSearchText = '';

    switch (locationType) {
      case CompletionSuggestionType.Neighborhood.name: {
        formattedSearchText = `
          ${location.name} ${StringExtensions.toCapitalizedCase(locationType)},
          ${StringExtensions.toCapitalizedCase(location.marketName)},
          ${StringExtensions.toCapitalizedCase(location.marketState)}
        `;
        formattedSearchText = StringExtensions.normalizeSpaces(formattedSearchText);
        break;
      }
      case CompletionSuggestionType.County.name: {
        formattedSearchText = `${location.name} County, ${location.marketState}`;
        break;
      }
      case CompletionSuggestionType.Zip.name: {
        formattedSearchText = `${location.name}`;
        break;
      }
      case CompletionSuggestionType.City.name: {
        formattedSearchText = `${location.city}, ${location.marketState}`;
        break;
      }
      case CompletionSuggestionType.SchoolDistrict.name: {
        formattedSearchText = `${location.name}`;
        break;
      }
      case CompletionSuggestionType.Market.name: {
        const stateAbbreviation = location.marketState;
        formattedSearchText = `${location.marketName}, ${stateAbbreviation} Area`;
        break;
      }
      case CompletionSuggestionType.State.name: {
        formattedSearchText = `${location.name}`;
        break;
      }
      default: {
        formattedSearchText = '';
        break;
      }
    }

    const normalizedText = StringExtensions.normalizeSpaces(formattedSearchText);
    return normalizedText;
  }

  /**
   * Toggles the scroll behavior of an element by adding or removing the 'nhs-u-overflow--hidden' class.
   * @param {HTMLElement} element - The element to toggle scroll behavior on.
   * @param {boolean} disable - Whether to disable or enable scrolling.
   */
  static toggleScroll(element, disable) {
    if (!element || !element.classList) {
      return;
    }

    element.classList.toggle('nhs-u-overflow--hidden', disable);
  }
}